import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Save, Camera, Trash2, Plus, Users, Award, BookOpen, X, ImageIcon, Mail, Share2, Download, Link as LinkIcon, Globe } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { HuntChecklist } from "@/components/HuntChecklist";
import { HuntPacking } from "@/components/HuntPacking";
import { HuntCamp } from "@/components/HuntCamp";
import { HuntCardView } from "@/components/HuntCardView";
import { PartnerRecommendations } from "@/components/PartnerRecommendations";
import { SPECIES, WEAPONS, STYLES } from "@/lib/hunterOptions";

const overline = "hr-overline text-blaze text-[11px]";
const sectionCls = "bg-white/[0.04] border border-white/10 rounded-xl p-6";
const inputCls = "w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone placeholder:text-bone/30";
const STATUSES = ["planning", "active", "completed"];
const HARVEST = [["", "Not tracked"], ["unfilled", "Unfilled / unsuccessful"], ["success", "Filled tag / harvest"]];
const VIS = ["private", "party", "public"];
const ROLES = ["hunter", "guest", "guide", "outfitter", "organizer"];

function Field({ label, children }) {
  return <label className="block"><span className="text-bone/50 text-xs uppercase tracking-wider">{label}</span><div className="mt-1.5">{children}</div></label>;
}

export default function HuntRecord() {
  const { huntId } = useParams();
  const navigate = useNavigate();
  const { isAuthed, ready, user } = useAuth();
  const [data, setData] = useState(null);
  const [hunt, setHunt] = useState(null);
  const [log, setLog] = useState(null);
  const [party, setParty] = useState([]);
  const [savingHunt, setSavingHunt] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "hunter" });
  const [invite, setInvite] = useState({ email: "", role: "hunter" });
  const [tab, setTab] = useState("overview");
  const [cardBusy, setCardBusy] = useState(false);
  const fileRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (ready && !isAuthed) navigate("/", { replace: true });
  }, [ready, isAuthed, navigate]);

  useEffect(() => {
    if (!isAuthed) return;
    api.huntLog(huntId).then((d) => {
      setData(d); setHunt(d.hunt); setLog(d.log); setParty(d.party || []);
    }).catch(() => { toast.error("Hunt not found."); navigate("/passport"); });
  }, [isAuthed, huntId, navigate]);

  const isOwner = data?.is_owner;
  const setH = (k, v) => setHunt((h) => ({ ...h, [k]: v }));
  const setL = (k, v) => setLog((l) => ({ ...l, [k]: v }));

  const saveHunt = async () => {
    setSavingHunt(true);
    try {
      await api.updateHunt(huntId, {
        title: hunt.title, species: hunt.species, jurisdiction: hunt.jurisdiction,
        unit: hunt.unit, weapon: hunt.weapon, hunt_type: hunt.hunt_type,
        start_date: hunt.start_date, end_date: hunt.end_date,
        duration_days: hunt.duration_days ? Number(hunt.duration_days) : null,
        conditions: hunt.conditions, status: hunt.status,
        is_historical: !!hunt.is_historical, approximate: !!hunt.approximate,
      });
      toast.success("Hunt details saved.");
    } catch { toast.error("Couldn't save."); }
    setSavingHunt(false);
  };

  const saveLog = async () => {
    setSavingLog(true);
    try {
      const l = await api.updateHuntLog(huntId, {
        notes: log.notes, story: log.story, lessons_learned: log.lessons_learned,
        harvest_result: log.harvest_result, harvest_species: log.harvest_species,
        harvest_score: log.harvest_score, harvest_details: log.harvest_details,
        passport_visibility: log.passport_visibility, trophy_visibility: log.trophy_visibility,
      });
      setLog(l);
      toast.success("Your hunt log saved.");
    } catch { toast.error("Couldn't save."); }
    setSavingLog(false);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.uploadHuntPhoto(huntId, file);
      setLog((l) => ({ ...l, photos: r.photos }));
      toast.success("Photo added.");
    } catch { toast.error("Upload failed."); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = async (pid) => {
    const r = await api.deleteHuntPhoto(huntId, pid);
    setLog((l) => ({ ...l, photos: r.photos }));
  };

  const addMember = async () => {
    if (!newMember.name.trim()) return;
    const r = await api.addPartyMember(huntId, newMember);
    setParty(r.party);
    setNewMember({ name: "", email: "", role: "hunter" });
    toast.success("Party member added.");
  };

  const removeMember = async (mid) => {
    const r = await api.removePartyMember(huntId, mid);
    setParty(r.party);
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) return;
    try {
      const r = await api.inviteHunter(huntId, { email: invite.email.trim(), role: invite.role });
      setParty(r.party);
      setInvite({ email: "", role: "hunter" });
      toast.success("Invitation sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite.");
    }
  };

  const cardData = log && hunt ? {
    hunter: user?.display_name || "A HomeRange hunter", title: hunt.title,
    species: log.harvest_species || hunt.species, state: hunt.jurisdiction,
    year: (hunt.start_date || "").slice(0, 4), weapon: hunt.weapon,
    harvest: log.harvest_result === "success", score: log.harvest_score || "",
    story: log.story || "", photo: log.photos?.[0]?.url || null,
  } : null;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setCardBusy(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#1a1a17", skipFonts: true });
      const a = document.createElement("a"); a.download = `homerange-${(hunt.species || "hunt").toLowerCase()}.png`; a.href = url; a.click();
      toast.success("Hunt card downloaded.");
    } catch { toast.error("Couldn't generate image."); }
    setCardBusy(false);
  };

  const toggleCardPublic = async () => {
    const r = await api.toggleHuntCard(huntId, !log.card_public);
    setLog((l) => ({ ...l, card_public: r.card_public, card_id: r.card_id }));
    toast.success(r.card_public ? "Public share link enabled." : "Share link turned off.");
  };

  const copyCardLink = async () => {
    const url = `${window.location.origin}/hunt-card/${log.card_id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied."); } catch { toast(url); }
  };

  if (!ready || !isAuthed || !hunt) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }

  return (
    <div className="bg-charcoal min-h-screen grain text-bone" data-testid="hunt-record-page">
      <SiteHeader />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <Link to="/passport" className="inline-flex items-center gap-2 text-sm text-bone/50 hover:text-bone transition-colors" data-testid="back-to-passport"><ArrowLeft size={15} /> Hunt Passport</Link>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${hunt.status === "completed" ? "bg-forest/30 text-forest" : hunt.status === "active" ? "bg-blaze/20 text-blaze" : "bg-white/10 text-bone/60"}`} data-testid="hunt-status-badge">{hunt.status}</span>
          {hunt.is_historical && <span className="text-xs px-3 py-1 rounded-full bg-saddle/20 text-saddle/90">Historical</span>}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3" data-testid="hunt-title">{hunt.title}</h1>
        <p className="text-bone/50 mt-2">{[hunt.species, hunt.jurisdiction, hunt.unit].filter(Boolean).join(" · ")}</p>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 border-b border-white/10 overflow-x-auto" data-testid="workspace-tabs">
          {[["overview", "Overview"], ["prepare", "Prepare"], ["party", "Party"], ["camp", "Camp"], ["log", "Log & Photos"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? "border-blaze text-bone" : "border-transparent text-bone/50 hover:text-bone"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (<>
        {/* Hunt details (owner-editable) */}
        <div className={`${sectionCls} mt-8`} data-testid="section-hunt-details">
          <span className={overline}>Hunt details {isOwner ? "" : "(view only)"}</span>
          <fieldset disabled={!isOwner} className="mt-4 grid sm:grid-cols-2 gap-4 disabled:opacity-70">
            <Field label="Title"><input value={hunt.title || ""} onChange={(e) => setH("title", e.target.value)} className={inputCls} data-testid="hd-title" /></Field>
            <Field label="Status"><select value={hunt.status || "planning"} onChange={(e) => setH("status", e.target.value)} className={inputCls} data-testid="hd-status">{STATUSES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}</select></Field>
            <Field label="Species"><input list="species-list" value={hunt.species || ""} onChange={(e) => setH("species", e.target.value)} className={inputCls} data-testid="hd-species" /><datalist id="species-list">{SPECIES.map((s) => <option key={s} value={s} />)}</datalist></Field>
            <Field label="State"><input value={hunt.jurisdiction || ""} onChange={(e) => setH("jurisdiction", e.target.value)} className={inputCls} data-testid="hd-state" /></Field>
            <Field label="Unit / area"><input value={hunt.unit || ""} onChange={(e) => setH("unit", e.target.value)} placeholder="If known" className={inputCls} data-testid="hd-unit" /></Field>
            <Field label="Weapon"><input list="weapon-list" value={hunt.weapon || ""} onChange={(e) => setH("weapon", e.target.value)} className={inputCls} data-testid="hd-weapon" /><datalist id="weapon-list">{WEAPONS.map((s) => <option key={s} value={s} />)}</datalist></Field>
            <Field label="Hunt type"><input list="type-list" value={hunt.hunt_type || ""} onChange={(e) => setH("hunt_type", e.target.value)} className={inputCls} data-testid="hd-type" /><datalist id="type-list">{STYLES.map((s) => <option key={s} value={s} />)}</datalist></Field>
            <Field label="Days afield"><input type="number" value={hunt.duration_days || ""} onChange={(e) => setH("duration_days", e.target.value)} placeholder="If known" className={inputCls} data-testid="hd-days" /></Field>
            <Field label="Start date"><input type="date" value={hunt.start_date || ""} onChange={(e) => setH("start_date", e.target.value)} className={inputCls} data-testid="hd-start" /></Field>
            <Field label="End date"><input type="date" value={hunt.end_date || ""} onChange={(e) => setH("end_date", e.target.value)} className={inputCls} data-testid="hd-end" /></Field>
            <Field label="Conditions / weather"><input value={hunt.conditions || ""} onChange={(e) => setH("conditions", e.target.value)} placeholder="e.g. Cold front, fresh snow" className={inputCls} data-testid="hd-conditions" /></Field>
            <label className="flex items-center gap-2 self-end pb-2"><input type="checkbox" checked={!!hunt.is_historical} onChange={(e) => setH("is_historical", e.target.checked)} data-testid="hd-historical" /><span className="text-sm text-bone/70">Historical hunt (partial info OK)</span></label>
          </fieldset>
          {isOwner && <button onClick={saveHunt} disabled={savingHunt} className="mt-5 inline-flex items-center gap-2 bg-white/10 hover:bg-blaze hover:text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50" data-testid="save-hunt-details">{savingHunt ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save details</button>}
        </div>
        {(hunt.jurisdiction || hunt.species) && (
          <PartnerRecommendations state={hunt.jurisdiction} species={hunt.species} theme="dark" className="mt-8" />
        )}
        </>)}

        {tab === "prepare" && (
          <div className="mt-6 space-y-6">
            <div className={sectionCls}><HuntChecklist huntId={huntId} /></div>
            <div className={sectionCls}><HuntPacking huntId={huntId} /></div>
          </div>
        )}

        {tab === "camp" && (
          <div className={`${sectionCls} mt-6`} data-testid="section-camp">
            <HuntCamp huntId={huntId} />
          </div>
        )}

        {tab === "log" && (<>
        {/* My log */}
        <div className={`${sectionCls} mt-5`} data-testid="section-my-log">
          <div className="flex items-center gap-2"><BookOpen size={15} className="text-blaze" /><span className={overline}>Your hunt log</span></div>
          <p className="text-bone/40 text-xs mt-2">Private to you by default. Unsuccessful hunts are complete Passport entries too — a harvest is never required.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Harvest result"><select value={log.harvest_result || ""} onChange={(e) => setL("harvest_result", e.target.value)} className={inputCls} data-testid="log-harvest-result">{HARVEST.map(([v, l]) => <option key={v} value={v} className="bg-charcoal">{l}</option>)}</select></Field>
            {log.harvest_result === "success" && <>
              <Field label="Harvest species / animal"><input value={log.harvest_species || ""} onChange={(e) => setL("harvest_species", e.target.value)} placeholder="e.g. 6x6 Bull Elk" className={inputCls} data-testid="log-harvest-species" /></Field>
              <Field label="Score / measurements (optional)"><input value={log.harvest_score || ""} onChange={(e) => setL("harvest_score", e.target.value)} placeholder="Optional" className={inputCls} data-testid="log-harvest-score" /></Field>
              <Field label="Harvest details (optional)"><input value={log.harvest_details || ""} onChange={(e) => setL("harvest_details", e.target.value)} placeholder="Optional" className={inputCls} data-testid="log-harvest-details" /></Field>
            </>}
          </div>
          <div className="mt-4 space-y-4">
            <Field label="The story"><textarea rows={4} value={log.story || ""} onChange={(e) => setL("story", e.target.value)} placeholder="How the hunt unfolded…" className={inputCls} data-testid="log-story" /></Field>
            <Field label="Lessons learned"><textarea rows={2} value={log.lessons_learned || ""} onChange={(e) => setL("lessons_learned", e.target.value)} placeholder="What you'd do differently" className={inputCls} data-testid="log-lessons" /></Field>
            <Field label="Private notes"><textarea rows={2} value={log.notes || ""} onChange={(e) => setL("notes", e.target.value)} placeholder="Only you can see these" className={inputCls} data-testid="log-notes" /></Field>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Passport visibility"><select value={log.passport_visibility || "private"} onChange={(e) => setL("passport_visibility", e.target.value)} className={inputCls} data-testid="log-passport-vis">{VIS.map((v) => <option key={v} value={v} className="bg-charcoal">{v}</option>)}</select></Field>
            {log.harvest_result === "success" && <Field label="Trophy Room visibility"><select value={log.trophy_visibility || "private"} onChange={(e) => setL("trophy_visibility", e.target.value)} className={inputCls} data-testid="log-trophy-vis">{VIS.map((v) => <option key={v} value={v} className="bg-charcoal">{v}</option>)}</select></Field>}
          </div>
          <p className="text-bone/40 text-xs mt-3 flex items-center gap-1.5"><Lock size={11} /> Exact hunting locations are always private and never shared automatically.</p>
          <button onClick={saveLog} disabled={savingLog} className="mt-4 inline-flex items-center gap-2 bg-blaze text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="save-log">{savingLog ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save log</button>
        </div>

        {/* Photos */}
        <div className={`${sectionCls} mt-5`} data-testid="section-photos">
          <div className="flex items-center gap-2"><Camera size={15} className="text-blaze" /><span className={overline}>Photos</span></div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(log.photos || []).map((p) => (
              <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden bg-white/5" data-testid={`photo-${p.id}`}>
                <img src={mediaSrc(p.url)} alt={p.caption || "Hunt"} className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(p.id)} className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" data-testid={`remove-photo-${p.id}`}><X size={13} /></button>
              </div>
            ))}
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="aspect-square rounded-lg border-2 border-dashed border-white/15 hover:border-blaze grid place-items-center text-bone/40 hover:text-blaze transition-colors" data-testid="add-photo">
              {uploading ? <Loader2 size={22} className="animate-spin" /> : <span className="flex flex-col items-center gap-1"><ImageIcon size={22} /><span className="text-xs">Add photo</span></span>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="photo-input" />
          </div>
        </div>

        {/* Shareable Hunt Card */}
        <div className={`${sectionCls} mt-5`} data-testid="section-hunt-card">
          <div className="flex items-center gap-2"><Share2 size={15} className="text-blaze" /><span className={overline}>Shareable Hunt Card</span></div>
          <p className="text-bone/40 text-xs mt-2">A beautiful card of this hunt — shows state only, never your unit, coordinates or private notes.</p>
          <div className="mt-5">
            <HuntCardView ref={cardRef} card={cardData} />
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={downloadCard} disabled={cardBusy} className="inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="card-download">{cardBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download image</button>
            <button onClick={toggleCardPublic} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${log.card_public ? "bg-forest/30 text-forest" : "border border-white/15 text-bone/80 hover:text-bone"}`} data-testid="card-toggle-public"><Globe size={15} /> {log.card_public ? "Public link on" : "Create public link"}</button>
            {log.card_public && log.card_id && (
              <button onClick={copyCardLink} className="inline-flex items-center gap-2 border border-white/15 text-bone/80 hover:text-bone px-5 py-2.5 rounded-full text-sm font-semibold transition-colors" data-testid="card-copy-link"><LinkIcon size={15} /> Copy link</button>
            )}
          </div>
        </div>
        </>)}

        {tab === "party" && (<>
        {/* Hunting party */}
        <div className={`${sectionCls} mt-5`} data-testid="section-party">
          <div className="flex items-center gap-2"><Users size={15} className="text-blaze" /><span className={overline}>Hunting party</span></div>
          <div className="mt-4 space-y-2">
            {party.map((m) => (
              <div key={m.id} className="flex items-center justify-between bg-white/5 border border-white/10 rounded px-4 py-2.5" data-testid={`party-${m.id}`}>
                <div>
                  <span className="text-sm font-semibold text-bone">{m.display_name || m.name || "Member"}</span>
                  <span className="text-bone/40 text-xs ml-2">{m.role}{m.status === "owner" ? " · owner" : m.status === "manual" ? " · not on HomeRange" : ` · ${m.status}`}</span>
                </div>
                {isOwner && m.status !== "owner" && <button onClick={() => removeMember(m.id)} className="text-bone/40 hover:text-red-400 transition-colors" data-testid={`remove-party-${m.id}`}><Trash2 size={15} /></button>}
              </div>
            ))}
          </div>
          {isOwner && (
            <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
              <input value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} placeholder="Name" className={inputCls} data-testid="party-name" />
              <input value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder="Email (optional)" className={inputCls} data-testid="party-email" />
              <select value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} className={inputCls} data-testid="party-role">{ROLES.map((r) => <option key={r} value={r} className="bg-charcoal">{r}</option>)}</select>
              <button onClick={addMember} className="bg-white/10 hover:bg-blaze hover:text-white rounded px-4 py-2.5 transition-colors" data-testid="party-add"><Plus size={17} /></button>
            </div>
          )}
          <p className="text-bone/40 text-xs mt-3">Manual members are remembered even without a HomeRange account.</p>

          {isOwner && (
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2"><Mail size={14} className="text-blaze" /><span className={overline}>Invite a HomeRange hunter</span></div>
              <div className="mt-3 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                <input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="Their HomeRange email" className={inputCls} data-testid="invite-email" />
                <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className={inputCls} data-testid="invite-role">{ROLES.map((r) => <option key={r} value={r} className="bg-charcoal">{r}</option>)}</select>
                <button onClick={sendInvite} className="bg-blaze text-white rounded px-5 py-2.5 text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="invite-send">Invite</button>
              </div>
              <p className="text-bone/40 text-xs mt-3 flex items-center gap-1.5"><Lock size={11} /> Invitees accept before joining. Joining never exposes anyone&apos;s private profile, points, or exact locations.</p>
            </div>
          )}
        </div>
        </>)}

        {log.harvest_result === "success" && (
          <div className="mt-5 flex items-center gap-2 text-forest text-sm" data-testid="trophy-note"><Award size={15} /> This harvest can appear in your Trophy Room.</div>
        )}
      </main>
      <Footer />
    </div>
  );
}
